import Image from 'next/image';
import Link from 'next/link';
import { useMsal } from '@azure/msal-react';
import { usePathname } from 'next/navigation';
import useAdminGuard from '@/hooks/useAdminGuard';
import { loginRequest } from '@/authConfig';
import { Box, Button, Stack, styled, Toolbar, UserAvatar } from '@/primitives';
import {
  APP_ROUTE_NAMES,
  APP_ROUTE_PATHS,
  PUBLIC_ROUTES,
} from '@/configs/routes';
import useIsPublicSearch from '@/hooks/useIsPublicSearch';
import useIsRegistryRoute from '@/hooks/useIsRegistryRoute';
import { black, blue, slate } from '@/lib/colours';

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

interface Props {
  onNavigate?: () => void;
}

export default function Navigation({ onNavigate }: Props) {
  const isPublicSearch = useIsPublicSearch();
  const { instance } = useMsal();
  const isRegistryRoute = useIsRegistryRoute();
  const pathname = usePathname();
  const { isAuthenticated, handleClickLink } = useAdminGuard(
    APP_ROUTE_PATHS.DASHBOARD,
  );

  return (
    <Toolbar
      component="nav"
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'start', md: 'center' },
        gap: '16px',
        px: { xs: 0, md: '16px' },
        height: '100%',
      }}
    >
      <LinkStyled
        href={PUBLIC_ROUTES.HOME}
        sx={{
          position: { md: 'absolute' },
          left: { md: '24px' },
          padding: 0,
        }}
      >
        <Image
          alt="BreedLOGIC Logo"
          height={37}
          width={176}
          priority
          src="/breedlogic-logo.svg"
        />
      </LinkStyled>

      <Stack
        direction="row"
        sx={{
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          gap: { xs: '16px', md: '8px' },
          margin: { md: 'auto' },
          width: { md: 'fit-content' },
          a: {
            justifyContent: { xs: 'start', md: 'center' },
            padding: { xs: '8px 4px', md: '0 8px' },
            height: { xs: '40px', md: '48px' },
            width: 'fit-content',
            minWidth: 0,
            fontSize: '14px',
          },
        }}
      >
        <Button
          component={LinkStyled}
          href={PUBLIC_ROUTES.HOME}
          sx={{
            color: pathname === PUBLIC_ROUTES.HOME ? blue[600] : black[950],
          }}
          onClick={onNavigate}
        >
          {APP_ROUTE_NAMES.HOME}
        </Button>
        <Button
          component={LinkStyled}
          href={PUBLIC_ROUTES.SEARCH}
          sx={{
            color: isPublicSearch ? blue[600] : black[950],
          }}
          onClick={onNavigate}
        >
          {APP_ROUTE_NAMES.SEARCH}
        </Button>
        <Button
          component={LinkStyled}
          href={APP_ROUTE_PATHS.DASHBOARD}
          sx={{
            color: isRegistryRoute ? blue[600] : black[950],
          }}
          onClick={() => {
            handleClickLink();
            onNavigate?.();
          }}
        >
          {APP_ROUTE_NAMES.REGISTRY}
        </Button>
      </Stack>

      {isAuthenticated ? (
        <Box
          sx={{
            position: { md: 'absolute' },
            right: { md: '24px' },
            display: 'flex',
            border: `1px solid ${slate[200]}`,
            borderRadius: '4px',
            padding: '4px 8px',
            width: { xs: '100%', md: 'auto' },
            minWidth: '260px',
          }}
        >
          <UserAvatar />
        </Box>
      ) : (
        <Button
          sx={{
            position: { md: 'absolute' },
            right: { md: '24px' },
            height: '40px',
            width: { xs: '100%', md: 'auto' },
          }}
          variant="contained"
          onClick={() => {
            instance.loginRedirect(loginRequest);
            onNavigate?.();
          }}
        >
          Sign in
        </Button>
      )}
    </Toolbar>
  );
}
